import type { FunctionComponent } from 'react';
import { useMemo, useState } from 'react';

import { intl } from '@trello/i18n';
import { TrelloMarkdown } from '@trello/markdown';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import { SearchForm } from 'app/src/components/Navigation/SearchForm';
import { useMemberLocaleQuery } from './MemberLocaleQuery.generated';
import { usePublicPluginsQuery } from './PublicPluginsQuery.generated';
import { createSearchIndex } from './SearchHelper';

export const SearchWrapper: FunctionComponent = () => {
  const markdown = new TrelloMarkdown();
  const [searchValue, setSearchValue] = useState('');
  const { data: memberLocaleQuery } = useMemberLocaleQuery();
  const { data } = usePublicPluginsQuery({
    variables: {
      preferredLocales:
        dangerouslyConvertPrivacyString(
          memberLocaleQuery?.member?.prefs?.locale,
        ) || 'en',
    },
    waitOn: ['MemberHeader'],
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const plugins = data?.publicPlugins ? data.publicPlugins : [];
  const filteredPublicPlugins = useMemo(
    () =>
      plugins.filter(
        (plugin) =>
          !plugin.moderatedState ||
          !['hidden', 'moderated'].includes(plugin.moderatedState),
      ),
    [plugins],
  );
  const searchIndex = useMemo(
    () => createSearchIndex(filteredPublicPlugins),
    [filteredPublicPlugins],
  );
  const searchResults = searchIndex.search(searchValue);

  const results = searchResults.map(({ id, listing, icon }) => {
    let subtext = '';

    if (listing) {
      if (listing.overview) {
        subtext = listing.overview || '';
      } else if (listing.description) {
        subtext = markdown.text(listing.description).output;
      }
    }

    return {
      label: listing ? listing.name : '',
      value: {
        identifier: id,
        iconUrl: icon.url,
        subtext,
      },
    };
  });

  return (
    <SearchForm
      placeholder={intl.formatMessage({
        id: 'templates.directory.find-power-up',
        defaultMessage: 'Find Power-Up',
        description:
          'Locates and activates available Power-Up features in the directory.',
      })}
      results={results}
      setSearchValue={setSearchValue}
      searchValue={searchValue}
      resultUrlSlug={'power-ups'}
      searchUrlSlug={'power-ups'}
      filterOption={() => true} // Show all results
    />
  );
};
